import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useSearchParams, useNavigate } from 'react-router-dom';
import phoneIcon from '../assets/phoneIcon.svg';

const Directory = ({apiUrl}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [visitorId, setVisitorId] = useState(null);
  const [guard, setGuard] = useState(false);
  const [withinRadius, setWithinRadius] = useState(false);
  const [geoSettled, setGeoSettled] = useState(false);
  const hasFetchedContacts = useRef(false);
  const fetchedWithoutGeo = useRef(false);
  const fetchSeq = useRef(0);

  // Extract visitor ID from query parameters
  useEffect(() => {
    const id = searchParams.get('id') || searchParams.toString();
    setVisitorId(id);
  }, [searchParams]);

  // Handle back button click
  const handleBack = () => {
    navigate(`/?${searchParams.toString()}`);
  };

  // Check whether the gatehouse call option should be shown
  useEffect(() => {
    if (!apiUrl) return;

    const checkGuard = async () => {
      try {
        const response = await axios.post(
          `${apiUrl}/intercom/show`,
          {
            string: `fX${window.location.href.split('fX')[1]}`,
          },
          { timeout: 10000 }
        );
        if (response.status === 200 && response.data.display) {
          setGuard(!!response.data.guard);
        }
      } catch (err) {
        console.error('Error checking gatehouse availability:', err);
      }
    };

    checkGuard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  // Navigate to the call screen for a gatehouse call
  const handleGatehouseClick = () => {
    navigate(`/call?${searchParams.toString()}`, {
      state: {
        guard: true,
        address: 'Gatehouse',
      }
    });
  };

  // Request geolocation on mount and keep watching for updates
  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoSettled(true);
      return;
    }

    const successHandler = (position) => {
      setLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy || 100,
      });
      setGeoSettled(true);
    };

    const errorHandler = (err) => {
      // Don't block the directory: fetch without geo and let the server decide
      console.error('Geolocation error:', err);
      setGeoSettled(true);
    };

    // Call getCurrentPosition first to reliably trigger the permission prompt on Android
    navigator.geolocation.getCurrentPosition(successHandler, errorHandler, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 30000,
    });

    const watchId = navigator.geolocation.watchPosition(successHandler, errorHandler, {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 30000,
    });

    // The geolocation timeout doesn't run while the permission prompt is open,
    // so fall through on our own if the visitor never answers it
    const settleTimer = setTimeout(() => setGeoSettled(true), 10000);

    return () => {
      clearTimeout(settleTimer);
      navigator.geolocation.clearWatch(watchId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch contacts once geolocation has resolved either way (geo may be null).
  // If that first attempt went out without a fix and one arrives later, retry
  // once so a slow GPS doesn't cost the visitor the directory.
  useEffect(() => {
    if (!geoSettled) return;
    if (hasFetchedContacts.current && !(fetchedWithoutGeo.current && location)) return;

    hasFetchedContacts.current = true;
    fetchedWithoutGeo.current = !location;

    const geo = location;
    const seq = ++fetchSeq.current;

    const fetchContacts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.post(
          `${apiUrl}/intercom/populate`,
          {
            string: `fX${window.location.href.split('fX')[1]}`,
            geo,
          },
          { timeout: 10000 }
        );
        // Ignore a stale attempt that a later retry has already superseded
        if (seq !== fetchSeq.current) return;
        setContacts(response.data);
        setWithinRadius(true);
      } catch (err) {
        console.error('Error fetching contacts:', err);
        if (seq !== fetchSeq.current) return;
        if (err.response && err.response.data === "Outside Gate Radius") {
          setWithinRadius(false);
          setError(`${err.response.data}`);
        } else {
          setError('Failed to fetch contacts. Please try again later.');
        }
      } finally {
        if (seq === fetchSeq.current) {
          setLoading(false);
        }
      }
    };

    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoSettled, location]);

  // Filter contacts based on search query
  useEffect(() => {
    if (searchQuery.length >= 1) {
      const filtered = contacts
        .filter(
          (contact) =>
            contact.address.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => {
          const numA = parseInt(a.address, 10);
          const numB = parseInt(b.address, 10);
          const aIsNum = !Number.isNaN(numA);
          const bIsNum = !Number.isNaN(numB);

          // Numeric addresses sort first, in numeric order
          if (aIsNum && bIsNum) {
            if (numA !== numB) return numA - numB;
            return a.address.localeCompare(b.address);
          }
          if (aIsNum) return -1;
          if (bIsNum) return 1;

          // Otherwise alphabetical
          return a.address.localeCompare(b.address);
        });
      setFilteredContacts(filtered);
    } else {
      setFilteredContacts([]);
    }
  }, [searchQuery, contacts]);

  const handleContactClick = (contact) => {
    setSelectedContact(contact);

    navigate(`/call?${searchParams.toString()}`, {
      state: {
        contactId: contact.id ?? '',
        address: contact.address
      }
    });
  };

  return (
    <DirectoryContainer>
      <TopBarDiv>
        <BackButton onClick={handleBack} style={{color: "white"}}>⬅</BackButton>
        <h1 style={{color: "white", fontSize: "1.5em"}}>Directory</h1>
      </TopBarDiv>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <SearchBarContainer>
        <SearchInput
          type="text"
          placeholder="Search By Address"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchBarContainer>

      {loading && !searchQuery && <LoadingMessage>Loading contacts...</LoadingMessage>}

      {searchQuery.length === 0 ? (
        <NoResultsMessage>Type an address to search</NoResultsMessage>
      ) : filteredContacts.length === 0 ? (
        <NoResultsMessage>No contacts found"{searchQuery}"</NoResultsMessage>
      ) : (
        <ContactList>
          {filteredContacts.map((contact, index) => (
            <ContactItem
              key={index}
              onClick={() => handleContactClick(contact)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleContactClick(contact);
                }
              }}
            >
              <ContactAddress>{contact.address}</ContactAddress>
              <img id="phoneicon" alt="phone icon" src={phoneIcon} style={{width: "1em", paddingRight: "0.5em"}}></img>
            </ContactItem>
          ))}
        </ContactList>
      )}

      {guard && withinRadius && (
        <GatehouseButton onClick={handleGatehouseClick}>
          <img id="phoneicon" alt="phone icon" src={phoneIcon} style={{width: "1.1em", paddingRight: "0.5em"}}></img>
          Call Gatehouse
        </GatehouseButton>
      )}
    </DirectoryContainer>
  );
};

export default Directory;


const DirectoryContainer = styled.div`
  width: 90%;
  max-width: 600px;
  margin: 0 auto;
  padding: 10px;
  font-family: Arial, sans-serif;
`;

const BackButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

const BackButton = styled.button`
  padding: 10px 20px 10px 5px;
  font-size: 16px;
  background-color: black;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #B70100;
  }

  &:active {
    background-color: #821010ff;
  }
`;

const SearchBarContainer = styled.div`
  margin-bottom: 20px;
  width: 100%;
`;

const TopBarDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  position: relative;
  // margin: 10px;
  margin-bottom: 30px;

  h1 {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    margin: 0;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 15px;
  box-sizing: border-box;
  transition: border-color 0.3s;
  background-color: black;
  font-weight: 600;
  color: white;

  &:focus {
    outline: none;
    border: 2.5px solid #ddd;
  }
`;

const ContactList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ContactItem = styled.li`
  padding: 12px;
  padding-left: 20px;
  padding-right: 15px;
  border: 0.2px solid gray;
  border-radius: 15px;
  margin-top: 25px;
  margin-bottom: 25px;
  background-color: black;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background-color: #f5f5f5;
  }

  // &:last-child {
  //   border-bottom: 1px solid #ddd;
  // }
`;

const ContactName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const ContactAddress = styled.div`
  font-size: 1em;
  color: #fff;
  font-weight: 600;
  max-width: 25ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const GatehouseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 30px auto 0 auto;
  padding: 0.8em 1.4em;
  font-size: 1em;
  font-weight: bold;
  color: white;
  background-color: black;
  border: 0.2px solid gray;
  border-radius: 15px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #B70100;
  }

  &:active {
    background-color: #821010ff;
  }
`;

const NoResultsMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
`;

const LoadingMessage = styled.div`
  padding: 20px;
  text-align: center;
  color: #666;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background-color: #ffebee;
  color: #c62828;
  border-radius: 4px;
  margin-bottom: 20px;
`;